import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { getAuthToken } from '../utils/auth';

const PING_INTERVAL_MS  = 25_000;
const PONG_TIMEOUT_MS   = 10_000;
const RECONNECT_DELAYS  = [1000, 2000, 5000, 10_000, 30_000];

/**
 * Normalize the configured WebSocket base URL into a handshake endpoint.
 * Accepts both origin-only values (`wss://host`) and `/ws`-suffixed values
 * (`wss://host/ws`) so a minor env typo does not silently break realtime.
 */
function buildWsHandshakeUrl(rawBaseUrl, token) {
  const fallback = 'ws://localhost:8080';
  const source = String(rawBaseUrl ?? '').trim() || fallback;

  try {
    const url = new URL(source);
    url.hash = '';
    const normalizedPath = url.pathname.replace(/\/+$/, '');
    url.pathname = !normalizedPath || normalizedPath === '/' ? '/ws' : normalizedPath;
    url.search = '';
    url.searchParams.set('token', token);
    return url.toString();
  } catch (_) {
    const compact = source.replace(/\s+/g, '');
    const withProtocol = /^wss?:\/\//i.test(compact) ? compact : `ws://${compact}`;
    const withoutTrailingSlash = withProtocol.replace(/\/+$/, '');
    const base = withoutTrailingSlash.endsWith('/ws') ? withoutTrailingSlash : `${withoutTrailingSlash}/ws`;
    return `${base}?token=${encodeURIComponent(token)}`;
  }
}

/**
 * Single shared WebSocket client. See CLAUDE.md → "Realtime / WebSocket Pattern".
 *
 * Usage:
 *   const { status, send, subscribe } = useRealtimeSocket();
 *   useEffect(() => subscribe('chat:42', (event) => { ... }), []);
 *
 * - Auto-connect when user is authenticated
 * - Auto-reconnect with exponential backoff (1s → 30s)
 * - Token refresh on 'Unauthorized' error frame (forces page reload to re-auth)
 * - PING/PONG keepalive every 25s; force reconnect if no PONG in 10s
 * - subscribe(room, handler) sends JOIN_ROOM and registers a per-room handler
 * - The hook is shared across the app — caller IDs distinguish handlers.
 */
export default function useRealtimeSocket() {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState('disconnected');

  const wsRef         = useRef(null);
  const handlersRef   = useRef(new Map());        // Map<room, Set<handler>>
  const globalRef     = useRef(new Set());        // handlers that want every event
  const pingTimerRef  = useRef(null);
  const pongTimerRef  = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const manualCloseRef      = useRef(false);
  const joinedRoomsRef      = useRef(new Set());

  const dispatch = useCallback((payload) => {
    const directThreadId = Number(payload?.thread_id ?? 0);
    const nestedThreadId = Number(payload?.message?.thread_id ?? 0);
    const room = payload?.room
      || (directThreadId > 0 ? `chat:${directThreadId}` : null);
    const nestedRoom = !room && nestedThreadId > 0
      ? `chat:${nestedThreadId}`
      : null;
    const targetRoom = room || nestedRoom;
    if (targetRoom && handlersRef.current.has(targetRoom)) {
      for (const h of handlersRef.current.get(targetRoom)) h(payload);
    }
    for (const h of globalRef.current) h(payload);
  }, []);

  const sendRaw = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(obj));
      return true;
    }
    return false;
  }, []);

  const startPing = useCallback(() => {
    clearInterval(pingTimerRef.current);
    pingTimerRef.current = setInterval(() => {
      if (!sendRaw({ type: 'PING' })) return;
      clearTimeout(pongTimerRef.current);
      pongTimerRef.current = setTimeout(() => {
        // No PONG — force reconnect
        try { wsRef.current?.close(); } catch (_) {}
      }, PONG_TIMEOUT_MS);
    }, PING_INTERVAL_MS);
  }, [sendRaw]);

  const connect = useCallback(() => {
    const token = getAuthToken();
    const wsUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:8080';
    if (!token) {
      setStatus('disconnected');
      return;
    }

    const url = buildWsHandshakeUrl(wsUrl, token);
    setStatus('connecting');
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus('connected');
      reconnectAttemptRef.current = 0;
      // Re-join rooms after reconnect
      for (const room of joinedRoomsRef.current) {
        ws.send(JSON.stringify({ type: 'JOIN_ROOM', room }));
      }
      startPing();
    };

    ws.onmessage = (event) => {
      let data = null;
      try { data = JSON.parse(event.data); } catch (_) { return; }
      if (!data || typeof data !== 'object') return;

      if (data.type === 'PONG') {
        clearTimeout(pongTimerRef.current);
        return;
      }
      if (data.type === 'ERROR' && /unauthorized/i.test(data.message || '')) {
        manualCloseRef.current = true;
        try { ws.close(); } catch (_) {}
        // Force page reload so auth context picks up fresh token (or kicks to /login)
        window.location.reload();
        return;
      }
      dispatch(data);
    };

    ws.onclose = () => {
      clearInterval(pingTimerRef.current);
      clearTimeout(pongTimerRef.current);
      wsRef.current = null;
      setStatus('disconnected');
      if (manualCloseRef.current) return;

      const attempt = Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS.length - 1);
      reconnectAttemptRef.current += 1;
      setTimeout(connect, RECONNECT_DELAYS[attempt]);
    };

    ws.onerror = () => {
      // onclose will follow — handled there
    };
  }, [dispatch, startPing]);

  useEffect(() => {
    if (!isAuthenticated) {
      manualCloseRef.current = true;
      try { wsRef.current?.close(); } catch (_) {}
      return undefined;
    }
    manualCloseRef.current = false;
    connect();
    return () => {
      manualCloseRef.current = true;
      try { wsRef.current?.close(); } catch (_) {}
      clearInterval(pingTimerRef.current);
      clearTimeout(pongTimerRef.current);
    };
  }, [isAuthenticated, connect]);

  /** Subscribe to a room. Returns an unsubscribe function. Auto-sends JOIN_ROOM. */
  const subscribe = useCallback((room, handler) => {
    if (!handlersRef.current.has(room)) handlersRef.current.set(room, new Set());
    handlersRef.current.get(room).add(handler);

    if (!joinedRoomsRef.current.has(room)) {
      joinedRoomsRef.current.add(room);
      sendRaw({ type: 'JOIN_ROOM', room });
    }

    return () => {
      const set = handlersRef.current.get(room);
      if (!set) return;
      set.delete(handler);
      if (set.size === 0) {
        handlersRef.current.delete(room);
        joinedRoomsRef.current.delete(room);
        sendRaw({ type: 'LEAVE_ROOM', room });
      }
    };
  }, [sendRaw]);

  /** Subscribe to ALL events (for notifications dropdown / toast). */
  const subscribeAll = useCallback((handler) => {
    globalRef.current.add(handler);
    return () => globalRef.current.delete(handler);
  }, []);

  const send = sendRaw;

  return { status, send, subscribe, subscribeAll };
}
